import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import Emblem from "./Emblem";
import DripCurtain from "./DripCurtain";

const VAN_URL = "/vans/side.png";

const SPLASH_URL =
    "https://images.pexels.com/photos/36251119/pexels-photo-36251119.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

/**
 * Masked line-by-line reveal. Each line is wrapped in overflow-hidden
 * and translated Y from 100% to 0%.
 */
function RevealLine({ children, delay = 0, className = "" }) {
    const reduce = useReducedMotion();
    return (
        <span className={`block overflow-hidden ${className}`}>
            <motion.span
                initial={reduce ? { y: 0 } : { y: "110%" }}
                animate={{ y: 0 }}
                transition={{
                    duration: 1.05,
                    ease: [0.19, 1, 0.22, 1],
                    delay,
                }}
                className="block will-change-transform"
            >
                {children}
            </motion.span>
        </span>
    );
}

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const vanY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const vanScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
    const splashY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
    const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

    // Hero cycles its own colour scheme every 12s (synced to the videos),
    // independent of the site's 45s global theme.
    // Hero flips to the other scheme exactly when the current video ENDS
    // (on the hand-and-towel wipe), so each clip plays fully — no loop/restart.
    // A generous fallback timer covers the rare case the 'ended' event misfires.
    const [heroTheme, setHeroTheme] = useState("dark");
    const flipHero = () =>
        setHeroTheme((t) => (t === "dark" ? "light" : "dark"));
    useEffect(() => {
        const fallbackMs = heroTheme === "dark" ? 34000 : 22000;
        const id = setTimeout(flipHero, fallbackMs);
        return () => clearTimeout(id);
    }, [heroTheme]);

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero"
            data-hero-theme={heroTheme}
            className="relative min-h-[100svh] w-full overflow-hidden pt-40 md:pt-48"
        >
            {/* High-res glossy paint dripping down behind everything */}
            <DripCurtain mode={heroTheme} onEnded={flipHero} />


            {/* Faint grid ticks on left */}
            <div className="hidden xl:flex flex-col absolute left-4 bottom-56 gap-5 z-10 font-mono text-[10px] tracking-[0.25em] text-white/35 uppercase [writing-mode:vertical-rl] rotate-180">
                <span>[ 01 ] BRAND · [ 02 ] FLEET · [ 03 ] SERVICES · [ 04 ] SOFLA · [ 05 ] MIDGE</span>
            </div>

            {/* Content grid */}
            <div className="relative z-20 grid grid-cols-1 lg:grid-cols-12 gap-8 px-6 md:px-16 pt-8 pb-16">
                {/* Left: Kinetic hero copy */}
                <motion.div
                    style={{ y: textY }}
                    className="lg:col-span-8 flex flex-col justify-between"
                >
                    <div>
                        {/* Micro badge */}
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15, duration: 0.6 }}
                            className="flex items-center gap-3 mb-8"
                        >
                            <Emblem size={38} spin />
                            <span className="font-mono text-[11px] tracking-[0.32em] uppercase text-white/60">
                                PAULs Fleet · South Florida · Since Day One
                            </span>
                        </motion.div>

                        {/* THE WORDMARK */}
                        <h1
                            data-testid="hero-headline"
                            className="font-display uppercase text-white text-[19vw] sm:text-[15vw] lg:text-[12.5vw] leading-[0.86] tracking-[-0.045em]"
                        >
                            <RevealLine delay={0.2}>PAINT</RevealLine>
                            <RevealLine delay={0.35}>
                                <span className="rainbow-text">KING</span>
                            </RevealLine>
                        </h1>

                        {/* Subhead */}
                        <div className="mt-8 max-w-2xl">
                            <RevealLine delay={0.75}>
                                <p className="font-display uppercase text-2xl md:text-3xl lg:text-4xl leading-[0.95] tracking-tight">
                                    Crown the color.
                                </p>
                            </RevealLine>
                            <RevealLine delay={0.85}>
                                <p className="font-display uppercase text-2xl md:text-3xl lg:text-4xl leading-[0.95] tracking-tight text-white/70">
                                    Rule the wall.
                                </p>
                            </RevealLine>
                        </div>

                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.05, duration: 0.7 }}
                            className="mt-8 max-w-xl font-mono text-[13px] leading-relaxed text-white/60"
                        >
                            <span className="text-white/90 font-display uppercase tracking-tight text-base md:text-lg block mb-2">
                                Fanatically detailed. With a passion for
                                preparation.
                            </span>
                            Paul's crew paints South Florida in colors it
                            hasn't dreamt yet — interiors, exteriors,
                            cabinets, drywall, pressure wash, wallpaper. If
                            it has a surface, we crown it.
                        </motion.p>
                    </div>

                    {/* CTAs */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.15, duration: 0.7 }}
                        className="mt-10 flex flex-col sm:flex-row gap-4 items-start"
                    >
                        <a
                            href="tel:5616984044"
                            data-testid="hero-phone-cta"
                            className="group glass-btn relative inline-flex items-center gap-4 border border-white px-7 py-5 font-mono text-xs md:text-sm tracking-[0.24em] uppercase overflow-hidden"
                        >
                            <span className="absolute inset-0 rainbow-bg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            <span className="relative z-10 flex items-center gap-4 group-hover:text-black transition-colors">
                                <span className="w-2 h-2 rounded-full bg-[#00FF85] blink" />
                                Call Paul — 561·698·4044
                                <span className="text-lg leading-none translate-y-[-1px] transition-transform duration-500 group-hover:translate-x-2">
                                    ↗
                                </span>
                            </span>
                        </a>

                        <a
                            href="#services"
                            data-testid="hero-services-link"
                            className="group glass-btn inline-flex items-center gap-3 border border-white/25 px-6 py-5 font-mono text-xs tracking-[0.24em] uppercase text-white/70 hover:text-white hover:border-white transition-colors"
                        >
                            See the work
                            <span className="transition-transform duration-500 group-hover:translate-y-1">
                                ↓
                            </span>
                        </a>
                    </motion.div>
                </motion.div>

                {/* Right: Fleet of vans stacked with parallax depth */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.4, delay: 0.6 }}
                    className="lg:col-span-4 relative"
                    data-testid="hero-fleet"
                >
                    <div className="relative aspect-[3/4] overflow-hidden">
                        {/* Grid ticks */}
                        <div className="absolute -left-1 top-0 bottom-0 flex flex-col justify-between font-mono text-[9px] tracking-[0.28em] uppercase text-white/25 py-6 z-10">
                            <span>PK · FLEET</span>
                            <span>03 · UNITS</span>
                            <span>NV200 · SOFLA</span>
                        </div>

                        {/* Stage floor lines — perspective */}
                        <div className="absolute inset-x-0 bottom-[10%] h-px bg-white/15" />
                        <div className="absolute inset-x-8 bottom-[35%] h-px bg-white/8" />
                        <div className="absolute inset-x-16 bottom-[58%] h-px bg-white/5" />

                        {/* Van 3 — farthest back, smallest, blurred */}
                        <motion.img
                            src={VAN_URL}
                            alt=""
                            aria-hidden
                            className="absolute left-[28%] top-[8%] w-[62%] object-contain grayscale-[0.35]"
                            style={{
                                y: useTransform(scrollYProgress, [0, 1], ["0%", "35%"]),
                                filter: "blur(1.4px) drop-shadow(0 12px 18px rgba(0,0,0,0.6))",
                                opacity: 0.45,
                            }}
                            initial={{ opacity: 0, x: 120 }}
                            animate={{ opacity: 0.45, x: 0 }}
                            transition={{
                                duration: 1.4,
                                delay: 1.4,
                                ease: [0.19, 1, 0.22, 1],
                            }}
                        />

                        {/* Van 2 — middle */}
                        <motion.img
                            src={VAN_URL}
                            alt=""
                            aria-hidden
                            className="absolute left-[10%] top-[24%] w-[82%] object-contain"
                            style={{
                                y: useTransform(scrollYProgress, [0, 1], ["0%", "22%"]),
                                filter: "drop-shadow(0 20px 25px rgba(0,0,0,0.7))",
                                opacity: 0.85,
                            }}
                            initial={{ opacity: 0, x: 80 }}
                            animate={{ opacity: 0.85, x: 0 }}
                            transition={{
                                duration: 1.4,
                                delay: 1.15,
                                ease: [0.19, 1, 0.22, 1],
                            }}
                        />

                        {/* Van 1 — front, largest, sharpest */}
                        <motion.img
                            src={VAN_URL}
                            alt="PaintKing Nissan NV200 fleet lineup"
                            className="absolute -left-[6%] top-[46%] w-[112%] max-w-none object-contain drop-shadow-[0_35px_40px_rgba(0,0,0,0.8)]"
                            style={{
                                y: vanY,
                                scale: vanScale,
                            }}
                            initial={{ opacity: 0, x: 40, rotate: -1 }}
                            animate={{ opacity: 1, x: 0, rotate: 0 }}
                            transition={{
                                duration: 1.4,
                                delay: 0.9,
                                ease: [0.19, 1, 0.22, 1],
                            }}
                        />

                        {/* Top decal */}
                        <div className="absolute top-2 right-2 border border-white/60 px-3 py-1.5 font-mono text-[10px] tracking-[0.3em] uppercase backdrop-blur-sm bg-black/50 z-20">
                            PK · UNITS 001–003
                        </div>

                        {/* Bottom label */}
                        <div className="absolute bottom-1 left-0 right-0 flex items-end justify-between px-2 z-20">
                            <div>
                                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/60">
                                    Nissan NV200 · Fleet of 3
                                </div>
                                <div className="font-display uppercase text-lg leading-none mt-1">
                                    The Rolling Palette
                                </div>
                            </div>
                            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#FFE600] flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#00FF85] blink" />
                                ROLLING
                            </div>
                        </div>
                    </div>

                    {/* Sticker */}
                    <motion.div
                        initial={{ opacity: 0, rotate: -6, scale: 0.6 }}
                        animate={{ opacity: 1, rotate: -8, scale: 1 }}
                        transition={{ delay: 1.6, duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
                        className="absolute -top-6 -left-6 md:-left-10 rainbow-bg text-black font-display uppercase text-xs tracking-tight px-4 py-2 border border-black z-30"
                    >
                        No drip · No drama
                    </motion.div>
                </motion.div>
            </div>

            {/* Bottom hero rail */}
            <div className="relative z-20 border-t border-white/10 mt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
                    {[
                        ["12+", "Years wrangling walls"],
                        ["100%", "SoFla licensed & insured"],
                        ["8", "Signature services"],
                        ["1", "Very happy Midge"],
                    ].map(([n, l], i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{
                                duration: 0.7,
                                delay: 0.1 * i,
                                ease: [0.19, 1, 0.22, 1],
                            }}
                            className="px-6 md:px-10 py-8 glass"
                        >
                            <div className="font-display text-4xl md:text-5xl">
                                {n}
                            </div>
                            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/50 mt-2">
                                {l}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
